/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, type ReactNode, useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import useControlled from '@atlaskit/ds-lib/use-controlled';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import { UniversalCreateBFFProvider } from '@atlassian/navigation-bff-data-provider/universal-create';
import { NavigationUFOSegment } from '@atlassian/navigation-perf-utils';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { MenuStateContext } from '../../../context';
import { GenericErrorBoundary } from '../../../services';
import type {
	MenuStateContextProps,
	UniversalCreateButtonCombinedProps,
	UniversalCreateButtonProps,
	UniversalCreateButtonPropsV2,
} from '../../../types';
import { useNormalizeItems, useIsResponsiveView } from '../../../utils';
import AnalyticsWrapper from '../../components/AnalyticsWrapper';
import { GicModalProvider } from '../../components/CrossAppItem/primitives/GicModalContext';
import { EntryPointPopup } from '../../components/EntryPointPopup';
import { LoadingMenu } from '../../components/LoadingMenu';
import { TriggerButton } from '../../components/TriggerButton';

import { popupContentEntryPoint } from './entrypoint';
import { useJsChunkLoadTrack } from './hooks/use-js-chunk-load-track';

/**
 * Type guard to check if props are v1 (has 'items' property)
 */
function isV1Props(props: UniversalCreateButtonCombinedProps): props is UniversalCreateButtonProps {
	return 'items' in props && props.buttonOnly !== true;
}

/**
 * Type guard to check if props are v2 (has 'sections' property)
 */
function isV2Props(
	props: UniversalCreateButtonCombinedProps,
): props is UniversalCreateButtonPropsV2 {
	return 'sections' in props && props.buttonOnly !== true;
}

function UniversalCreateButtonInner(props: UniversalCreateButtonCombinedProps) {
	const {
		children,
		testId,
		onClick,
		onClose,
		isOpen,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		onMouseEnter,
		onMouseLeave,
		shouldRenderToParent,
		buttonOnly = false,
	} = props;
	const isButtonOnly = buttonOnly && fg('navx_4208_uc_button_only_enabled');
	const primaryActionItem = 'primaryActionItem' in props ? props.primaryActionItem : undefined;
	const isResponsive = useIsResponsiveView();

	// Track JS chunk load time - must be called before the lazy chunk loads
	useJsChunkLoadTrack();

	// Normalize items for v1, or use empty array for v2
	const items = isV1Props(props) ? props.items : undefined;
	const structuredItems = useNormalizeItems(items ?? []);
	const isOpenStateRefactor = fg('navx-4182-uc-open-state-refactor');

	const [isLocalOpen, setLocalIsOpen] = useControlled(isOpen, () => false);

	const handleTriggerButtonClick = useCallback(() => {
		if (isOpenStateRefactor) {
			setLocalIsOpen(!isLocalOpen);
		}
		// Notify parent
		onClick?.();
	}, [isOpenStateRefactor, isLocalOpen, setLocalIsOpen, onClick]);

	const handleClose = useCallback(() => {
		if (isOpenStateRefactor) {
			setLocalIsOpen(false);
		}

		onClose?.();
	}, [isOpenStateRefactor, setLocalIsOpen, onClose]);

	const handleMouseEnter = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	const [growthRecommendationsSessionId, setGrowthRecommendationsSessionId] = useState<
		string | undefined
	>(undefined);

	const menuStateContextValue = useMemo((): MenuStateContextProps => {
		const isV2Mode = isV2Props(props);
		const sections = isV2Mode && 'sections' in props && props.sections ? props.sections : [];
		const crossAppCreationConfig =
			isV2Mode && 'crossAppCreationConfig' in props
				? (props.crossAppCreationConfig ?? ({ enabled: false } as const))
				: ({ enabled: false } as const);
		const selectedItem = 'selectedItem' in props ? props.selectedItem : undefined;
		const controlledOpen = 'isOpen' in props ? props.isOpen : undefined;

		return {
			isV2Mode,
			items: structuredItems,
			product: props.product ?? 'unknown',
			subProduct: props.subProduct,
			isNav4Enabled,
			sections,
			crossAppCreationConfig,
			suggestionConfig:
				'suggestionConfig' in props
					? (props.suggestionConfig ?? { enabled: false })
					: { enabled: false },
			selectedItem,
			preventClose: props.preventClose,
			primaryActionItem,
			growthRecommendationsSessionId,
			setGrowthRecommendationsSessionId,
			...(isOpenStateRefactor ? {} : { controlledOpen }),
		};
	}, [
		props,
		isNav4Enabled,
		structuredItems,
		primaryActionItem,
		isOpenStateRefactor,
		growthRecommendationsSessionId,
	]);

	const sharedTriggerButtonProps = useMemo(
		() => ({
			testId,
			triggerButtonSpotlightTarget,
			enableTriggerPulse,
			onMouseEnter: handleMouseEnter,
			onMouseLeave,
			onClick: handleTriggerButtonClick,
			isNav4Enabled,
			buttonTheme,
		}),
		[
			testId,
			triggerButtonSpotlightTarget,
			enableTriggerPulse,
			handleMouseEnter,
			onMouseLeave,
			handleTriggerButtonClick,
			isNav4Enabled,
			buttonTheme,
		],
	);

	return (
		<MenuStateContext.Provider value={menuStateContextValue}>
			<GicModalProviderWithFG>
				{isButtonOnly ? (
					<TriggerButton {...sharedTriggerButtonProps} buttonOnly>
						{children}
					</TriggerButton>
				) : (
					<EntryPointPopup
						{...(isOpenStateRefactor ? { isOpen: isLocalOpen } : {})}
						placement={
							(isResponsive && isNav4Enabled) || primaryActionItem ? 'bottom-end' : 'bottom-start'
						}
						// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
						shouldRenderToParent={shouldRenderToParent}
						onClose={handleClose}
						trigger={(triggerProps) => (
							<TriggerButton
								{...sharedTriggerButtonProps}
								buttonOnly={undefined} // explicitly disable buttonOnly
								isEntryPointEnabled
								triggerProps={triggerProps}
								isOpen={triggerProps['aria-expanded']}
								primaryActionItem={primaryActionItem}
							>
								{children}
							</TriggerButton>
						)}
						entryPoint={popupContentEntryPoint}
						entryPointProps={
							props as Exclude<UniversalCreateButtonCombinedProps, { buttonOnly: true }>
						}
						fallback={<LoadingMenu padded />}
					/>
				)}
			</GicModalProviderWithFG>
		</MenuStateContext.Provider>
	);
}

function UFOWrapper({ children }: { children: ReactNode }) {
	return <NavigationUFOSegment id="universalCreateComponentLoad">{children}</NavigationUFOSegment>;
}

function NoopComponent<T>(props: { children: ReactNode; value?: T | undefined }) {
	return <Fragment>{props.children}</Fragment>;
}

const GicModalProviderWithFG = componentWithFG('navx_enable_gic', GicModalProvider, NoopComponent);

function UniversalCreateButtonLazyInner(props: UniversalCreateButtonCombinedProps) {
	return (
		<UFOWrapper>
			<ConditionalRelayEnvironmentProvider>
				<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
					<UniversalCreateBFFProvider>
						<AnalyticsWrapper>
							<FlagsProvider>
								<UniversalCreateButtonInner {...props} />
							</FlagsProvider>
						</AnalyticsWrapper>
					</UniversalCreateBFFProvider>
				</BFFProvider>
			</ConditionalRelayEnvironmentProvider>
		</UFOWrapper>
	);
}

/**
 * A universal "Create" button used across Atlassian products to provide a consistent
 * content-creation experience. It renders a trigger button that, when clicked, opens a
 * popup menu listing creation options (e.g. pages, issues, goals) sourced from the
 * host product and optionally from cross-app integrations.
 *
 * The component supports two API versions via a discriminated union on its props:
 *
 * - **V1 (`items`)** – *(Deprecated)* Pass a flat or grouped list of menu items through the `items` prop.
 *   Optionally configure cross-app suggestions via `suggestionConfig`.
 *   Consumers should migrate to the V2 API.
 * - **V2 (`sections`)** – Pass structured `MenuSectionV2[]` through the `sections` prop.
 *   Optionally enable cross-app creation via `crossAppCreationConfig`.
 *
 * In both versions, setting `buttonOnly: true` renders just the trigger button without
 * the popup menu, which is useful for scenarios like Jira's global issue create.
 *
 * The component wraps its content with:
 * - An error boundary (`GenericErrorBoundary`) for resilient rendering.
 * - A UFO performance tracking segment for monitoring component load times.
 * - Relay and BFF (Backend-for-Frontend) data providers for remote data fetching.
 * - An analytics wrapper for instrumented event tracking.
 * - A flags provider for in-context messaging (e.g. success/error toasts).
 *
 * @example with Menu items
 * ```tsx
 * import { UniversalCreateButton } from '@atlassian/universal-create';
 * import type { UniversalCreateMenuSection } from '@atlassian/universal-create/types';
 *
 * const sections: UniversalCreateMenuSection[] = [
 *   { title: 'Confluence', items: [...] },
 *   { title: 'Jira', items: [...] },
 * ];
 *
 * <UniversalCreateButton product="confluence" sections={sections}>
 *   Create
 * </UniversalCreateButton>
 * ```
 *
 * @example Button-only mode (no popup)
 * ```tsx
 * <UniversalCreateButton product="jira" buttonOnly onClick={handleClick}>
 *   Create
 * </UniversalCreateButton>
 * ```
 *
 * @example With cross-app creation config
 * ```tsx
 *   const crossAppConfig: CrossAppCreationConfig = useMemo(
 *     () => ({
 *       enabled: true,
 *       prefetch: true // this enables hover to fetch API, otherwise API is fetch upon click
 *       cloudId,
 *       orgId,
 *       accountId,
 *     }),
 *     [cloudId, orgId, accountId],
 *   );
 *
 *   const sections = useMemo(
 *     () => [
 *       {
 *         type: 'appDefinedSection',
 *         items: [
 *           { ati: Ati.parse('ati:cloud:confluence:page'), children: 'Page', onClick: handleCreatePage },
 *           { ati: Ati.parse('ati:cloud:jira:issue'), type: 'link' },
 *         ],
 *       },
 *     ],
 *     [],
 *   );
 *
 *   return (
 *     <UniversalCreateButton
 *       product="confluence"
 *       sections={sections}
 *       crossAppCreationConfig={crossAppConfig}
 *     >
 *       Create
 *     </UniversalCreateButton>
 *   );
 * ```
 *
 * The `crossAppCreationConfig` prop accepts a {@link CrossAppCreationConfig} object which is a
 * discriminated union on the `enabled` field:
 * - When `enabled: true`, provide `cloudId`, `orgId`, and `accountId` so the component can
 *   call the V2 backend to hydrate cross-app items (e.g. Jira issues from within Confluence).
 *   Optional fields include `environment`, `prefetch`, `crossAppLinkOpenTarget`, and `onItemClick`.
 * - When `enabled: false`, no additional fields are required and cross-app items render statically.
 *
 * @param props - The component props, accepting either V1 ({@link UniversalCreateButtonProps})
 *   or V2 ({@link UniversalCreateButtonPropsV2}) configurations as a discriminated union.
 * @param props.product - The name of the consuming Atlassian product (e.g. `'confluence'`, `'jira'`).
 * @param props.children - The button label text (should be an i18n-translated string such as `'Create'`).
 * @param props.items - *(V1 only, deprecated)* The menu items to display in the creation popup. Use `sections` (V2) instead.
 * @param props.sections - *(V2 only)* Structured menu sections to display in the creation popup.
 * @param props.buttonOnly - When `true`, renders only the trigger button without popup content.
 * @param props.onClick - Optional callback invoked when the trigger button is clicked.
 * @param props.onClose - Optional callback invoked when the popup menu is closed.
 * @param props.isOpen - Optional controlled open state for the popup (mirrors `@atlaskit/popup`).
 * @param props.primaryActionItem - When provided, enables the split-button variant with a primary action.
 * @param props.crossAppCreationConfig - *(V2 only)* Configuration for cross-app creation. See {@link CrossAppCreationConfig}.
 * @param props.testId - Optional test ID passed to the underlying popup (mirrors `@atlaskit/popup`).
 *
 * @see {@link UniversalCreateButtonProps} for the full V1 props definition.
 * @see {@link UniversalCreateButtonPropsV2} for the full V2 props definition.
 * @see {@link CrossAppCreationConfig} for the cross-app creation config shape.
 */
export function UniversalCreateButton(props: UniversalCreateButtonCombinedProps) {
	return (
		<GenericErrorBoundary name="universal-create-button-lazy" app={props.product}>
			<UniversalCreateButtonLazyInner {...props} />
		</GenericErrorBoundary>
	);
}
